import { useEffect, useRef, useState } from "react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import AppIconButton from "../../../components/form/AppIconButton";

async function writeClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.left = "-9999px";
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand("copy");
  area.remove();
  if (!ok) throw new Error("copy failed");
}

/** Copies one chat bubble's text. Used by the report chat and the embedded chatbot, which share this screen. */
export default function ChatCopyButton({ text, sx }: { text: string; sx?: SxProps<Theme> }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      if (timer.current != null) window.clearTimeout(timer.current);
    },
    []
  );

  return (
    <AppIconButton
      size="small"
      busyIgnore
      title={copied ? t("chat.copied") : t("chat.copy")}
      onClick={() => {
        void writeClipboard(text)
          .then(() => {
            setCopied(true);
            if (timer.current != null) window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => setCopied(false), 2000);
          })
          .catch(() => setCopied(false));
      }}
      sx={sx}
    >
      {copied ? <CheckRoundedIcon fontSize="small" /> : <ContentCopyRoundedIcon fontSize="small" />}
    </AppIconButton>
  );
}
